/**
 * Base API Client
 *
 * Type-safe HTTP client wrapper around axios.
 * Provides consistent error handling and request/response interceptors.
 *
 * Token Management:
 * - Tokens are stored in HttpOnly cookies (not accessible to JavaScript)
 * - Client-side requests go through /api/* proxy routes which read the cookie
 * - Server-side requests can pass a serverToken explicitly
 */

import { captureError } from '@/shared/lib/errors/capture';
import { getRequiredEnv } from '@/shared/lib/config/env';
import { redirect } from '@/shared/utils/redirect';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  type AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { parseDeprecatedFields } from '../lib/parse-deprecated-fields';

const IS_DEV = process.env.NODE_ENV !== 'production';
const seenDeprecations = new Set<string>();

function warnOnDeprecatedFields(response: AxiosResponse): void {
  if (!IS_DEV) return;
  const headerValue = response.headers['x-deprecated-fields'];
  if (typeof headerValue !== 'string' || headerValue.length === 0) return;
  const method = response.config.method?.toUpperCase() ?? 'GET';
  const url = response.config.url ?? '';
  parseDeprecatedFields(headerValue).forEach(({ field, reason, sunset }) => {
    const key = `${method}:${url}:${field}`;
    if (seenDeprecations.has(key)) return;
    seenDeprecations.add(key);
    const suffix = sunset ? ` (sunset: ${sunset})` : '';
    console.warn(`[deprecation] ${method} ${url} — field "${field}": ${reason}${suffix}`);
  });
}

const getBaseURL = (): string => {
  if (globalThis.window !== undefined) {
    // Client-side: use /api routes which proxy to backend
    // The proxy reads HttpOnly cookie and adds Authorization header
    return '/api';
  }
  // Server-side: direct API calls
  return getRequiredEnv('NEXT_PUBLIC_API_URL');
};

export interface RequestConfig extends AxiosRequestConfig {
  params?: Record<string, unknown> | undefined;
  serverToken?: string;
}

let isRedirecting = false;
let pendingSessionVerification: Promise<boolean> | null = null;

async function verifySessionIsInvalid(): Promise<boolean> {
  if (pendingSessionVerification) return pendingSessionVerification;
  pendingSessionVerification = (async () => {
    try {
      const res = await fetch('/api/me', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const data = await res.json();
      return data.authenticated !== true;
    } catch (error) {
      captureError(error, {
        component: 'ApiClient',
        action: 'parse-response',
        severity: 'warning',
        skipPostHog: true,
      });
      return false; // Network error — don't logout
    } finally {
      setTimeout(() => {
        pendingSessionVerification = null;
      }, 2000);
    }
  })();
  return pendingSessionVerification;
}

async function coordinatedLogout(): Promise<void> {
  if (isRedirecting) return;

  // Loop guard: if already on sign-in, the cleanup happened (or the user is
  // mid-login) — don't re-navigate.
  if (globalThis.window !== undefined && window.location.pathname.startsWith('/sign-in')) {
    return;
  }

  // Only logout when Zustand currently claims the user IS authenticated. If
  // it's already false (guest browsing, or checkAuth already reset it), the
  // 401 is expected — skip. Intentionally no longer gated on `isInitialized`:
  // stale-cookie scenarios can fire 401s during the hydration window before
  // checkAuth completes, and skipping them was masking the desync.
  const { useAuthStore } = await import('@/shared/stores/auth-store');
  const { isAuthenticated: authDone } = useAuthStore.getState();
  if (!authDone) return;

  isRedirecting = true;

  // Safety: reset flag if redirect doesn't complete (e.g., blocked by extension)
  setTimeout(() => {
    isRedirecting = false;
  }, 5000);

  try {
    // Clear Zustand state — persist middleware auto-syncs to localStorage
    useAuthStore.setState({
      user: null,
      isSubscribed: false,
      isProfileComplete: false,
      isAuthenticated: false,
      isInitialized: false,
      isLoading: false,
    });

    // Don't call /api/logout here — automatic 401-triggered logout should only
    // clear local state and redirect, NOT invalidate the token on the backend.
    // Backend token invalidation only happens on explicit user-initiated logout.

    // Preserve destination so the user returns here after signing back in.
    // Skip the redirect param on root and on auth pages to avoid noise / loops.
    const currentPath = window.location.pathname + window.location.search;
    const shouldPreserve =
      currentPath !== '/' &&
      !currentPath.startsWith('/sign-in') &&
      !currentPath.startsWith('/register') &&
      !currentPath.startsWith('/forgot-password') &&
      !currentPath.startsWith('/reset-password');
    const signInUrl = shouldPreserve
      ? `/sign-in?redirect=${encodeURIComponent(currentPath)}`
      : '/sign-in';

    // Redirect — full page load starts fresh
    window.location.href = signInUrl;
  } catch (error) {
    captureError(error, {
      component: 'ApiClient',
      action: 'parse-response',
      severity: 'warning',
      skipPostHog: true,
    });
    window.location.href = '/sign-in';
  }
}

class ApiClient {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      // Dev: cold Turbopack compiles queue the first requests for 60–90s
      // (worse after a .next cleanup) — 30s would error + retry-storm them.
      timeout: process.env.NODE_ENV === 'development' ? 120_000 : 30_000,
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Lazy baseURL resolution — avoids reading env vars at module import time
        // (which breaks CI builds where NEXT_PUBLIC_API_URL isn't set)
        if (!config.baseURL) {
          config.baseURL = getBaseURL();
        }
        // For server-side direct calls, add Authorization header if serverToken provided
        const serverToken = (config as RequestConfig).serverToken;
        if (serverToken && config.headers) {
          config.headers.Authorization = `Bearer ${serverToken}`;
        }
        // For client-side, the /api proxy routes handle Authorization

        // Client-context header for the backend (users.client_platform). Matters
        // on server-side direct calls to Laravel; on browser calls the /api proxy
        // strips it and re-stamps via getProxyHeaders().
        if (config.headers) {
          config.headers['X-Client-Platform'] = 'web';
        }

        // Apple/Play store policy hides "sensitive" content by default in the API
        // response; web is allowed to show it. mature=true opts every read into the
        // unfiltered set. GET-only so mutations/auth POSTs stay clean; per-call
        // params still win via the spread so a caller can override if ever needed.
        if (config.method?.toLowerCase() === 'get') {
          config.params = { mature: true, ...config.params };
        }

        return config;
      },
      (error: AxiosError) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response) => {
        warnOnDeprecatedFields(response);
        return response;
      },
      async (error: AxiosError) => {
        if (error.response) {
          warnOnDeprecatedFields(error.response);
        }
        try {
          if (error.response?.status === 401) {
            const requestUrl = error.config?.url || '';
            // Always allow /me to fail without triggering logout
            if (requestUrl.includes('/me')) {
              return Promise.reject(error);
            }
            if (globalThis.window !== undefined) {
              // Gate on Zustand's current `isAuthenticated` claim rather than
              // `isInitialized`. When a user with stale persisted state (auth
              // cookie gone but localStorage still says authenticated) hits a
              // 401, we want to reconcile immediately instead of waiting for
              // checkAuth — the race where queries fired during hydration and
              // the logout got silently dropped is exactly this bug.
              // Guests (isAuthenticated=false) hitting an authed endpoint still
              // short-circuit here, so we don't ping /me for them.
              const { useAuthStore } = await import('@/shared/stores/auth-store');
              const { isAuthenticated } = useAuthStore.getState();
              if (isAuthenticated) {
                const sessionInvalid = await verifySessionIsInvalid();
                if (sessionInvalid) {
                  coordinatedLogout();
                }
              }
            }
          }

          if (error.response?.status === 403 && globalThis.window !== undefined) {
            // Subscription-wall detection is a string-shape dependency on the
            // backend (no structured code field yet) — scan the common fields
            // case-insensitively so a copy tweak doesn't silently degrade the
            // redirect into a generic error toast (FRONT-306).
            const data = error.response.data as { message?: string; error?: string; code?: string };
            const text =
              `${data?.message ?? ''} ${data?.error ?? ''} ${data?.code ?? ''}`.toLowerCase();
            if (text.includes('subscription') || text.includes('subscribe')) {
              redirect('/choose-plan');
            }
          }
        } catch (redirectError) {
          captureError(redirectError, { component: 'ApiClient', action: 'response-interceptor' });
        }

        return Promise.reject(error);
      }
    );
  }

  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    const { data } = await this.client.get<T>(endpoint, config);
    return data;
  }

  async post<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    const response = await this.client.post<T>(endpoint, data, config);
    return response.data;
  }

  async put<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    const response = await this.client.put<T>(endpoint, data, config);
    return response.data;
  }

  async patch<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    const response = await this.client.patch<T>(endpoint, data, config);
    return response.data;
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    const response = await this.client.delete<T>(endpoint, config);
    return response.data;
  }
}

export const apiClient = new ApiClient();
